import { PaginationProps, TableProps } from 'antd';
import { deleteApi, getApi } from 'apis';
import { SideDrawerWrapper } from 'components/Global';
import TableActionHeader from 'components/Global/TableActionHeader';
import ConfirmDelete from 'components/Global/confirmDeleteModel';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { toastText } from 'utils/utils';
import AddDoseFormBody from './AddDoseFormBody';
import CustomTable from './CustomTable';
import { useDebounce } from 'utils/hooks';


const DoseFormTable = () => {

	// State variables
	const [drawerAnimation, setDrawerAnimation] = useState<boolean>(false);
	const [isSideDrawerOpen, setSideDrawerOpen] = useState<boolean>(false);
	const [search, setSearch] = useState('');
	const [page, setPage] = useState(1);
	const [limit, setLimit] = useState(10);
	const [filter, setFilter] = useState('');
	const [sort, setSort] = useState('');
	const [type, setType] = useState('asc');
	const [drawerInfo, setDrawerInfo] = useState({
		drawerTitle: 'Dose Form',
	});
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [editSelectedDoseForm, setEditSelectedDoseForm] = useState<any>();
	const [totalDoseForm, setTotalDoseForm] = useState(0);
	const [doseFormData, setDoseFormData] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [isDeleteLoading, setIsDeleteLoading] = useState<boolean>(false);
	const [isSearchCancel, setIsSearchCancel] = useState<boolean>(false);

	const debouncedSearch = useDebounce(search, 500);

	const removeDrawerFromDom = () => {
		setSideDrawerOpen(false);
	};

	const openDrawerHandler = () => {
		setDrawerInfo({ drawerTitle: 'Add Dose Form' });
		setDrawerAnimation(true);
		setSideDrawerOpen(true);
	};


	const closeDrawerByAnimation = () => {
		setEditSelectedDoseForm(undefined); //!isAddUserLoading &&
		setDrawerAnimation(false); //!isAddUserLoading &&
	};


	const performSearchHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setSearch(value);
		setPage(1);
		if (value.length > 0) {
			setIsSearchCancel(true)
		} else {
			setIsSearchCancel(false)
		}
	};


	const showModal = () => {
		setIsModalOpen(true);
	};

	const setDrawerInfoHandler = (drawerTitle: any) => {
		setDrawerInfo({ drawerTitle });
	};

	const handleOk = () => {
		setIsModalOpen(false);
		setEditSelectedDoseForm(null);
	};

	const handleCancel = () => {
		setIsModalOpen(false); // !isAddUserLoading &&
		setEditSelectedDoseForm(null);
	};

	const deleteHandler = async () => {
		if (editSelectedDoseForm) {
			try {
				setIsDeleteLoading(true);
				const resp = await deleteApi(`/dose-form/delete/${editSelectedDoseForm?.id}`);	
				toastText(resp?.data?.message ?? 'dose-form deleted successfully', 'success');
			} catch (error: any) {
				toastText(error.response?.data?.message ?? 'Something went wrong in delete dose-form', 'error');
			} finally {
				setIsDeleteLoading(false);
				handleCancel();
				fetchDoseForm();
			}
		}
	};

	const fetchDoseForm = useCallback(async () => {
		setIsLoading(true);
		const query = {
			search: debouncedSearch,
			sort: sort,
			type: type,
			filter: filter
		};
		try {
			const res = await getApi('/dose-form/get-all', query);
			setDoseFormData(res?.data?.data);
			setTotalDoseForm(res?.data?.total);
		} catch (error: any) {
			toastText(error?.response?.data?.error?.errorDescription, 'error');
		} finally {
			setIsLoading(false);
		}

	}, [debouncedSearch, type, sort, filter]);

	const handleTableChange: TableProps<any>['onChange'] = (
		pagination,
		filters,
		sorter
	) => {
		const sortOrder = Array.isArray(sorter)
			? sorter[0]?.order
			: sorter.order === 'ascend'
				? 'asc'
				: 'desc';
		setType(sortOrder as string);

		const sortField = Array.isArray(sorter)
			? sorter[0]?.field
			: sorter.field === 'name'
				? 'firstName'
				: sorter.field;
		setSort(sortField as string);
	};

	const onShowSizeChange: PaginationProps['onShowSizeChange'] = (
		current,
		pageSize
	) => {
		setPage(current);
		setLimit(pageSize);
	};


	useEffect(() => {
		fetchDoseForm();
	}, [debouncedSearch, sort, type, filter, fetchDoseForm]);

	// JSX
	return (
		<>
			<div className={''}>
				<TableActionHeader>
				</TableActionHeader>

				<CustomTable
					dataSource={doseFormData}
					handleTableChange={handleTableChange}
					onShowSizeChange={onShowSizeChange}
					isLoading={isLoading}
					search={search}
					totalRecords={totalDoseForm}
					performSearchHandler={performSearchHandler}
					performFilterHandler={(value: any) => setFilter(value)}
					openDrawerHandler={openDrawerHandler}
					setDrawerInfoHandler={setDrawerInfoHandler}
					setEditSelectedDoseForm={setEditSelectedDoseForm}
					showModal={showModal}
					currentPage={page}
					pageSize={limit}
					isSearchCancel={isSearchCancel}
					setIsSearchCancel={setIsSearchCancel}
					setSearch={setSearch}
				/>
			</div>
			<ConfirmDelete
				handleCancel={handleCancel}
				handleOk={handleOk}
				isModalOpen={isModalOpen}
				deleteHandler={deleteHandler}
				isLoading={isDeleteLoading} 
				selectedDeleteData={editSelectedDoseForm?.name}
				deleteHeading="Dose Form"
			/>
			{isSideDrawerOpen && (
				<SideDrawerWrapper
					isOpen={drawerAnimation}
					removeDrawerFromDom={removeDrawerFromDom}
					closeDrawerByAnimation={closeDrawerByAnimation}
					headerTitle={drawerInfo.drawerTitle}
					position="right"
					width="half"
				>
					<AddDoseFormBody
						closeDrawerByAnimation={closeDrawerByAnimation}
						editSelectedDoseForm={editSelectedDoseForm}
						fetchDoseForm={fetchDoseForm}
					/>
				</SideDrawerWrapper>
			)}
		</>
	);
};

export default DoseFormTable;
